import type { FocusEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import type {
  ImageValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { MediaImage } from '@elfsight/widget-media';
import {
  createReadableClass,
  roundTo4,
  textColorMixin,
  textStyleMixin
} from '@elfsight/widget-utils';
import { LINE_HEIGHT_RATIO } from '../constants';
import { ImageOptionButton, Overlay } from './image-option-button';

interface ImageOptionProps {
  option?: {
    name: string;
    caption: string;
    image: ImageValue;
    id: string;
  };
  label?: string;
  id?: string;
  required?: boolean;
  multiple?: boolean;
  itemWidth?: number;
  aspectRatio?: number;
  alignment?: 'left' | 'center' | 'right';
  fieldColor?: string;
  fieldFontStyle?: TextStyleValue;
  fieldHintColor?: string;
  fieldHintFontStyle?: TextStyleValue;
  fieldBorderType?: string;
  fieldBorderWidth?: number;
  fieldBorderColor?: string;
  fieldBorderRadius?: number;
  fieldFillColor?: string;
  fieldActiveColor?: string;
  fieldActiveFillColor?: string;

  value?: string[];

  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
}

export function ImageOption({
  option,
  itemWidth,
  value,
  aspectRatio,
  alignment,
  fieldColor,
  fieldFontStyle,
  fieldHintColor,
  fieldHintFontStyle,
  fieldBorderType,
  fieldBorderWidth,
  fieldBorderColor,
  fieldBorderRadius,
  fieldFillColor,
  fieldActiveColor,
  fieldActiveFillColor,
  onFocus,
  onBlur,
  ...forwardingProps
}: ImageOptionProps) {
  const [focused, setFocused] = useState(false);
  const imageBorderRadius = useMemo(() => {
    if (fieldBorderRadius) {
      return fieldBorderRadius > 4 ? fieldBorderRadius - 4 : 2;
    }
    return 0;
  }, [fieldBorderRadius]);

  const buttonSize = Math.round(Number(fieldFontStyle?.fontSize) * 1.2);

  const imageRatio =
    aspectRatio ||
    (option?.image?.width && option?.image?.height
      ? option.image?.height / option.image?.width
      : 1);

  const hasHeader = !!(option?.name || option?.caption);
  const isRounded = Number(fieldBorderRadius) > 14;
  const isChecked = Array.isArray(value)
    ? value.includes(String(option?.id))
    : value === option?.id;

  const handleFocused = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(e);
      }
      setFocused(true);
    },
    [onFocus]
  );
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      setFocused(false);
    },
    [onBlur]
  );

  return (
    <Item
      _width={itemWidth}
      _border={!!fieldBorderType}
      _borderColor={fieldBorderColor}
      _borderWidth={fieldBorderWidth}
      _borderRadius={fieldBorderRadius}
      _fillColor={fieldFillColor}
      _activeColor={fieldActiveColor}
      _isActiveItem={isChecked}
      _hasHeader={hasHeader}
      _isRounded={isRounded}
      _buttonSize={buttonSize}
      _focus={focused}
      _isChecked={isChecked}
    >
      <ImageOptionButton
        option={option}
        value={value}
        buttonSize={buttonSize}
        isChecked={isChecked}
        isRounded={isRounded}
        fieldColor={fieldColor}
        fieldBorderColor={fieldBorderColor}
        fieldBorderType={fieldBorderType}
        fieldBorderWidth={fieldBorderWidth}
        fieldActiveColor={fieldActiveColor}
        fieldActiveFillColor={fieldActiveFillColor}
        fieldFillColor={fieldFillColor}
        onFocus={handleFocused}
        onBlur={handleBlur}
        {...forwardingProps}
      />
      <Image
        src={option?.image?.url}
        alt={option?.name}
        mode={aspectRatio === 0 ? 'contain' : 'cover'}
        ratio={imageRatio}
        _borderRadius={imageBorderRadius}
      />
      {hasHeader && (
        <ItemHeader _alignment={alignment} _isRounded={isRounded}>
          {!!option.name && (
            <ItemLabel
              _textColor={fieldColor}
              _textStyle={fieldFontStyle}
              _hasHint={!!option.caption}
            >
              {option.name}
            </ItemLabel>
          )}
          {!!option.caption && (
            <ItemHint
              _textColor={fieldHintColor}
              _textStyle={fieldHintFontStyle}
            >
              {option.caption}
            </ItemHint>
          )}
        </ItemHeader>
      )}
    </Item>
  );
}

export const Text = styled.div.attrs<{
  _textStyle?: TextStyleValue;
  _textColor?: string;
}>({
  className: createReadableClass({
    component: 'image-option',
    element: 'text'
  })
})`
  ${textColorMixin}
  ${textStyleMixin}
  line-height: ${({ _textStyle }) =>
    roundTo4(Number(_textStyle?.fontSize) * LINE_HEIGHT_RATIO)}px;
`;

const Item = styled.label.attrs<{
  _isRounded: boolean;
  _buttonSize: number;
  _hasHeader: boolean;
  _isActiveItem: boolean;
  _focus: boolean;
  _isChecked: boolean;
  _width?: number;
  _fillColor?: string;
  _borderRadius?: number;
  _border?: boolean;
  _borderWidth?: number;
  _activeColor?: string;
  _borderColor?: string;
}>(({ _isChecked }) => ({
  className: createReadableClass({
    component: 'image-option',
    index: _isChecked && 'checked'
  })
}))`
  position: relative;
  cursor: pointer;
  box-sizing: border-box;
  min-height: ${({ _isRounded, _buttonSize }) =>
    _buttonSize + 2 * (_isRounded ? 16 : 8)}px;
  padding: 4px 4px ${({ _hasHeader }) => (_hasHeader ? 8 : 4)}px;
  width: ${({ _width }) => _width}px;
  background: ${({ _fillColor }) => _fillColor};
  border-radius: ${({ _borderRadius }) => _borderRadius}px;

  ${({ _border, _borderWidth, _isActiveItem, _activeColor, _borderColor }) =>
    `border: ${_border ? _borderWidth : 2}px solid
    ${
      _isActiveItem ? _activeColor : _border ? _borderColor : 'rgba(0, 0, 0, 0)'
    };`}

  ${({ _focus }) =>
    _focus &&
    css`
      & ${Overlay} {
        opacity: 0.1;
      }
    `}

  &:hover ${Overlay} {
    opacity: 0.1;
  }
`;

const Image = styled(MediaImage).attrs<{
  _borderRadius: number;
}>({
  className: createReadableClass({
    component: 'image-option',
    element: 'image'
  })
})`
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
`;

const ItemHeader = styled.div.attrs<{
  _alignment?: 'left' | 'center' | 'right';
  _isRounded?: boolean;
}>({
  className: createReadableClass({
    component: 'image-option',
    element: 'header'
  })
})`
  text-align: ${({ _alignment }) => _alignment};
  margin: 8px ${({ _isRounded }) => (_isRounded ? 12 : 0)}px 0;
`;
const ItemLabel = styled(Text)<{ _hasHint: boolean }>`
  margin-bottom: ${({ _hasHint }) => (_hasHint ? 4 : 0)}px;
`;
const ItemHint = styled(Text)``;
